import { FC, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IoLogOutOutline } from 'react-icons/io5';
import { PiBugBeetleThin, PiLightbulbThin } from 'react-icons/pi';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import { TbAd2, TbChalkboard, TbClockPause } from 'react-icons/tb';
// Kept for future use: Sentry feedback modal for "Report bug" (currently replaced by a Google Form link).
// import { feedbackIntegration } from '@sentry/react';
import { Menu, Button, Drawer } from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import ActivityStatusIndicator from 'components/ActivityStatusIndicator';
import AuthUserAvatar from 'components/AuthUserAvatar';
import appRoutes from 'config/appRoutes';
import { checkIsSuperUser } from 'config/permissions';
import { logout } from 'store/container/auth/auth-slice';
import { getRouteConfig, MenuItemBaseLinkType } from '../Sidebar/config';
import GlobalSearch from '../GlobalSearch';
import MenuItem from '../Sidebar/MenuItem';
import AvatarSheet from './AvatarSheet';

import style from './index.module.less';
import sideBarStyle from '../Sidebar/index.module.less';
import { UserResponse } from '../../../../types';
import { checkIsAdmin } from '../../../../utils/auth';

// Kept for future use: Sentry feedback modal for "Report bug" (currently replaced by a Google Form link).
// const sentryFeedback = feedbackIntegration({
//   colorScheme: 'system',
//   autoInject: false,
// });
//
// const sentryWidget = sentryFeedback.createWidget();
//
// sentryWidget.el.style.display = 'none';

const version = process.env.REACT_APP_GIT_HASH ? process.env.REACT_APP_GIT_HASH.substring(0, 6) : null;

interface UserDropdownProps {
  user: UserResponse;
  activeRoute?: MenuItemBaseLinkType;
}

const UserDropdown: FC<UserDropdownProps> = ({ user, activeRoute }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);

  const breakpoints = useBreakpoint();

  const fullScreen = !breakpoints.sm;

  const isRegularUser = checkIsAdmin(user);

  const button = (
    <div className={style.avatarContainer}>
      <Button type="link" size="large" style={{ padding: 0 }} onClick={fullScreen ? () => setMenuOpen(true) : undefined}>
        <AuthUserAvatar size="large" />
      </Button>
      <ActivityStatusIndicator />
    </div>
  );

  const profileItem = (
    <Menu.Item key="profile" icon={<HiOutlineUserCircle size={18} strokeWidth="1" />} onClick={() => setMenuOpen(false)}>
      <Link to={`${appRoutes.CONTACTS}/${user?.contact?.uuid}`}>Profile</Link>
    </Menu.Item>
  );

  const items = (
    <>
      {isRegularUser && (
        <Menu.Item key="sgm-sop" icon={<TbAd2 size={18} strokeWidth="1" />} onClick={() => setMenuOpen(false)}>
          <a
            // eslint-disable-next-line max-len
            href="https://www.figma.com/proto/ArQKrtkzWl0OmfDMw2qZVE/SGM-Guides-Internal?page-id=0%3A[…]ting-point-node-id=440%3A5128&node-id=440-5128&scaling=contain"
            target="_blank"
            rel="noreferrer"
          >
            SGM SOP&apos;s
          </a>
        </Menu.Item>
      )}

      {/*
        Kept for future use: "Report bug" opened the Sentry feedback modal instead of a link.
        <Menu.Item
          key="bug"
          icon={<PiBugBeetleThin size={18} />}
          onClick={() => {
            sentryWidget.el.click();
            setMenuOpen(false);
          }}
        >
          Report bug
        </Menu.Item>
      */}
      <Menu.Item key="bug" icon={<PiBugBeetleThin size={18} />} onClick={() => setMenuOpen(false)}>
        <a href="https://forms.gle/VdyqwBLPuBP7cVX29" target="_blank" rel="noreferrer">
          Report bug
        </a>
      </Menu.Item>

      <Menu.Item key="suggestions" icon={<PiLightbulbThin size={18} />} onClick={() => setMenuOpen(false)}>
        <a href="https://forms.gle/RZ7nT5D3voPfMrdH6" target="_blank" rel="noreferrer">
          Suggestions
        </a>
      </Menu.Item>

      {isRegularUser && (
        <Menu.Item key="time-off" icon={<TbClockPause size={18} strokeWidth="1" />} onClick={() => setMenuOpen(false)}>
          <a href="https://savageglobalmarketing.typeform.com/to/yZFxyRL5" target="_blank" rel="noreferrer">
            Request time-off
          </a>
        </Menu.Item>
      )}

      {isRegularUser && (
        <Menu.Item key="how-to" icon={<TbChalkboard size={18} strokeWidth="1" />} onClick={() => setMenuOpen(false)}>
          <Link to={appRoutes.HOW_TO}>Client how to&apos;s</Link>
        </Menu.Item>
      )}

      <Menu.Item key="divder-2" className="ant-menu-item-divider" />

      <Menu.Item
        key="logout"
        icon={<IoLogOutOutline size={18} />}
        onClick={(event) => {
          event.domEvent.stopPropagation();
          event.domEvent.preventDefault();
          setMenuOpen(false);
          dispatch(
            logout({
              onLoaded: () => history.push(appRoutes.LOGIN),
            }),
          );
        }}
      >
        Logout
      </Menu.Item>
    </>
  );

  if (!breakpoints.sm) {
    const isSuperUser = checkIsSuperUser(user?.email);
    const config = getRouteConfig();

    return (
      <>
        {button}
        <Drawer
          visible={menuOpen}
          title={<span className={style.menuTitle}>lionshare</span>}
          placement="right"
          width={600}
          className={style.drawerMenu}
          footer={version ? `Ver: ${version}` : undefined}
          onClose={() => setMenuOpen(false)}
        >
          <div className={sideBarStyle.sidebar} style={{ border: 'none' }}>
            <Menu mode="inline" theme="light" selectable={false}>
              <Menu.Item key="search" className={sideBarStyle.searchItem}>
                <GlobalSearch />
              </Menu.Item>

              {profileItem}

              <Menu.Item key="divder-1" className="ant-menu-item-divider" />

              {config.map((item) => (
                <MenuItem
                  key={`${'path' in item ? item.path : item.key}`}
                  item={item}
                  user={user}
                  classNameLabel={sideBarStyle.itemLabel}
                  isSuperUser={isSuperUser}
                  onClick={() => setMenuOpen(false)}
                  selectedItem={activeRoute && 'path' in activeRoute ? activeRoute : undefined}
                />
              ))}

              {items}
            </Menu>
          </div>
        </Drawer>
      </>
    );
  }

  return (
    <>
      <div className={style.avatarContainer}>
        <Button type="link" size="large" style={{ padding: 0 }} onClick={() => setMenuOpen(true)}>
          <AuthUserAvatar size="large" />
        </Button>
        <ActivityStatusIndicator />
      </div>
      <AvatarSheet
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        menuContent={
          <Menu mode="inline" theme="light" selectable={false}>
            {profileItem}
            {items}
          </Menu>
        }
      />
    </>
  );
};

export default UserDropdown;
